/**
 * Minimal message lookup: nested JSON catalogs, `{name}` interpolation and
 * plural keys (`key_one`, `key_other`, … selected with Intl.PluralRules).
 * Missing keys fall back to English, then to the key itself.
 */

export type Catalog = { [key: string]: string | Catalog };
export type Catalogs = Record<string, Catalog>;

function lookup(catalog: Catalog | undefined, key: string): string | undefined {
  let cur: string | Catalog | undefined = catalog;
  for (const part of key.split('.')) {
    if (!cur || typeof cur === 'string') return undefined;
    cur = cur[part];
  }
  return typeof cur === 'string' ? cur : undefined;
}

function interpolate(s: string, params: Record<string, unknown>): string {
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m));
}

export function createTranslator(catalogs: Catalogs, lang: string, fallback = 'en') {
  const base = lang.split('-')[0]!;
  const chain = [catalogs[lang], catalogs[base], catalogs[fallback]].filter(Boolean) as Catalog[];
  const plural = new Intl.PluralRules(lang);

  function find(key: string): string | undefined {
    for (const c of chain) {
      const v = lookup(c, key);
      if (v !== undefined) return v;
    }
    return undefined;
  }

  return function t(key: string, params: Record<string, unknown> = {}): string {
    if (typeof params.count === 'number') {
      const form = plural.select(params.count);
      const v = find(`${key}_${form}`) ?? find(`${key}_other`);
      if (v !== undefined) return interpolate(v, params);
    }
    const v = find(key);
    return v === undefined ? key : interpolate(v, params);
  };
}

/** Keys present in `reference` but missing from `catalog` — used by tests to keep catalogs complete. */
export function missingKeys(reference: Catalog, catalog: Catalog, prefix = ''): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(reference)) {
    const path = prefix ? `${prefix}.${k}` : k;
    const other = catalog[k];
    if (typeof v === 'string') {
      if (typeof other !== 'string' || !other.trim()) out.push(path);
    } else if (!other || typeof other === 'string') {
      out.push(path);
    } else {
      out.push(...missingKeys(v, other, path));
    }
  }
  return out;
}
